import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';

import { HelmRelease } from '@/react/kubernetes/helm/types';
import { HelmValuesInput } from '@/react/kubernetes/helm/components/HelmValuesInput';
import {
  ChartVersion,
  useHelmRepoVersions,
} from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRepoVersions';
import { useHelmChartValues } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartValues';
import { RegistryValue } from '@/react/kubernetes/helm/components/HelmRegistrySelect';
import { HelmVersionSelect } from '@/react/kubernetes/helm/components/HelmVersionSelect';
import { ManifestPreviewFormSection } from '@/react/kubernetes/helm/components/ManifestPreviewFormSection';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { FormControl } from '@@/form-components/FormControl';
import { Checkbox } from '@@/form-components/Checkbox';
import { Option } from '@@/form-components/PortainerSelect';
import { FormSection } from '@@/form-components/FormSection';

import { UpgradeHelmFormValues } from './types';
import { ChartReferenceFormSection } from './ChartReferenceFormSection';

interface Props {
  release: HelmRelease;
  chartReferenceInfoMessage?: string;
  isCheckingChartReference: boolean;
  initialRegistryValue?: RegistryValue;
  chartReferenceState: {
    requiresSourceConfirmation: boolean;
    requiresChartConfirmation: boolean;
  };
  setPreviewIsValid: (isValid: boolean) => void;
  hasInitialVersions: boolean;
}

export function ManualUpgradeFormSections({
  release,
  chartReferenceInfoMessage,
  isCheckingChartReference,
  initialRegistryValue,
  chartReferenceState,
  setPreviewIsValid,
  hasInitialVersions,
}: Props) {
  const environmentId = useEnvironmentId();
  const { values, setFieldValue, setValues, errors } =
    useFormikContext<UpgradeHelmFormValues>();

  const chartValuesRefQuery = useHelmChartValues({
    chart: values.chart,
    repo: values.repo,
    registryId: values.registryId,
    version: values.version,
    environmentId,
  });

  const versionOptionsQuery = useHelmVersionOptions(
    values,
    release.chart.metadata?.version ?? ''
  );
  const versionOptions = versionOptionsQuery.data ?? [];

  const selectedVersion =
    versionOptions.find((v) => v.value.version === values.version)?.value ||
    null;

  useAutoSelectVersion(
    versionOptions,
    (version) => setFieldValue('version', version),
    release.chart.metadata?.version,
    values.chart
  );

  return (
    <div>
      <ChartReferenceFormSection
        chartReferenceInfoMessage={chartReferenceInfoMessage}
        isCheckingChartReference={isCheckingChartReference}
        formValues={values}
        setValues={setValues}
        formErrors={{
          registryId: errors.registryId,
          chart: errors.chart,
        }}
        namespace={release.namespace}
        initialRegistryValue={initialRegistryValue}
        hasVersionOptions={hasInitialVersions || versionOptions.length > 0}
        chartReferenceState={chartReferenceState}
        releaseChartMetadata={release.chart.metadata}
        releaseChartPath={release.chartReference?.chartPath}
      />
      <FormSection title="Upgrade options">
        <FormControl
          label="Version"
          inputId="version-input"
          size="medium"
          errors={getNonBlockingVersionErrors(
            versionOptions,
            release.chart.metadata?.version ?? '',
            release.chart.metadata?.appVersion ?? '',
            values.chart
          )}
        >
          <HelmVersionSelect
            value={selectedVersion}
            options={versionOptions}
            onChange={(version) => setFieldValue('version', version?.version)}
            isClearable
            data-cy="helm-version-input"
            disabled={!values.chart}
            isLoading={versionOptionsQuery.isInitialLoading}
            isChartSelected={!!values.chart}
          />
        </FormControl>
        <FormControl
          label="Rollback on failure"
          tooltip="Enables automatic rollback on failure. It may increase the time to upgrade."
          inputId="atomic-input"
          size="medium"
        >
          <Checkbox
            id="atomic-input"
            checked={values.atomic}
            data-cy="atomic-checkbox"
            onChange={(e) => setFieldValue('atomic', e.target.checked)}
          />
        </FormControl>
        <HelmValuesInput
          values={values.values}
          setValues={(values) => setFieldValue('values', values)}
          valuesRef={chartValuesRefQuery.data?.values ?? ''}
          isValuesRefLoading={chartValuesRefQuery.isInitialLoading}
        />
      </FormSection>
      <div className="mb-10">
        <ManifestPreviewFormSection
          mode="manual"
          name={release.name}
          namespace={release.namespace || ''}
          chart={values.chart || ''}
          version={values.version || ''}
          repo={values.registryId ? undefined : values.repo}
          registryId={values.registryId}
          values={values.values || ''}
          onChangePreviewValidation={setPreviewIsValid}
          title="Manifest preview"
          environmentId={environmentId}
          currentManifest={release.manifest}
        />
      </div>
    </div>
  );
}

function getNonBlockingVersionErrors(
  versionOptions: Option<ChartVersion>[],
  releaseChartVersion: string,
  releaseAppVersion: string,
  chart: string
) {
  return (
    getReleaseVersionValidationMessage(
      versionOptions,
      releaseChartVersion,
      releaseAppVersion
    ) || getChartSelectMessage(chart)
  );
}

function getReleaseVersionValidationMessage(
  versionOptions: Option<ChartVersion>[],
  releaseChartVersion: string,
  releaseAppVersion: string
) {
  if (!releaseChartVersion || versionOptions.length === 0) {
    return '';
  }

  const versionOptionsValidation = versionOptions.reduce(
    (acc, version) => {
      const hasReleaseVersion = version.value.version === releaseChartVersion;
      const hasApplicationVersion =
        version.value.appVersion === releaseAppVersion;
      return {
        hasReleaseAndApplicationVersion:
          acc.hasReleaseAndApplicationVersion ||
          (hasReleaseVersion && hasApplicationVersion),
        hasReleaseVersion: acc.hasReleaseVersion || hasReleaseVersion,
        hasApplicationVersion:
          acc.hasApplicationVersion || hasApplicationVersion,
      };
    },
    {
      hasReleaseAndApplicationVersion: false,
      hasReleaseVersion: false,
      hasApplicationVersion: false,
    }
  );

  if (versionOptionsValidation.hasReleaseAndApplicationVersion) {
    return '';
  }

  if (versionOptionsValidation.hasReleaseVersion) {
    return `There is no version available with a matching app version '${releaseAppVersion}' for the release. Do you have the right chart selected?`;
  }

  if (versionOptionsValidation.hasApplicationVersion) {
    return `There is no version available with a matching Helm chart version '${releaseChartVersion}' for the release. Do you have the right chart selected?`;
  }

  return `There is no version available with a matching Helm chart version '${releaseChartVersion}' and app version '${releaseAppVersion}' for the release. Do you have the right chart selected?`;
}

function getChartSelectMessage(chart: string) {
  return chart ? '' : 'Please select a chart to load available versions.';
}

function useHelmVersionOptions(
  values: UpgradeHelmFormValues,
  releaseVersion: string
) {
  return useHelmRepoVersions(
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      select: (versions) =>
        versions?.map<Option<ChartVersion>>((version) => ({
          label:
            version.version === releaseVersion
              ? `${version.version} (current)`
              : version.version,
          value: version,
        })),
    },
    {
      chartPath: values.chart,
      repoURL: values.repo,
      registryID: values.registryId,
    },
    true
  );
}

function useAutoSelectVersion(
  options: Option<ChartVersion>[],
  setVersionValue: (versionValue: string) => void,
  releaseVersion?: string,
  chartValue?: string
): void {
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  // whenever the chart value updates, reset the auto selected version
  useEffect(() => {
    setHasAutoSelected(false);
  }, [chartValue]);

  useEffect(() => {
    if (!hasAutoSelected && options.length > 0 && releaseVersion) {
      if (!releaseVersion) {
        return;
      }
      const matchingVersion = options.find(
        (version) => version.value.version === releaseVersion
      );
      if (matchingVersion) {
        setVersionValue(matchingVersion.value.version);
        setHasAutoSelected(true);
      }
    }
  }, [hasAutoSelected, options, releaseVersion, setVersionValue]);
}
